/**
 * Calendar days, in the timezone the user is actually standing in.
 *
 * `new Date().toISOString().slice(0, 10)` is the tempting one-liner and it is
 * wrong east of Greenwich: before 05:30 IST it reports yesterday. Every date a
 * filter, a report period or a filename is built from goes through here.
 */
const pad = (n) => String(n).padStart(2, '0');

export const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** Today, here. */
export const today = () => ymd(new Date());

/** `days` before today, here. */
export const daysAgo = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return ymd(d);
};

/**
 * The first and last day of a month, `offset` months back. Built from the
 * calendar parts rather than by mutating a Date that still carries the current
 * time of day, which is what shifted GST return periods off by a day.
 */
export const monthStart = (offset = 0) => {
  const n = new Date();
  const d = new Date(n.getFullYear(), n.getMonth() - offset, 1);
  return ymd(d);
};

export const monthEnd = (offset = 0) => {
  const n = new Date();
  const d = new Date(n.getFullYear(), n.getMonth() - offset + 1, 0);
  return ymd(d);
};

/** A `YYYY-MM-DD` string back to a local Date at midnight, never via UTC. */
export const parseYmd = (s) => {
  const [y, m, d] = String(s).split('-').map(Number);
  return new Date(y, m - 1, d);
};

/** Whole calendar months a range covers, or 0 when it starts or ends mid-month. */
export const wholeMonths = (from, to) => {
  const f = parseYmd(from);
  const t = parseYmd(to);
  const lastOfTo = new Date(t.getFullYear(), t.getMonth() + 1, 0).getDate();
  if (f.getDate() !== 1 || t.getDate() !== lastOfTo) return 0;
  return (t.getFullYear() - f.getFullYear()) * 12 + t.getMonth() - f.getMonth() + 1;
};

/**
 * The same-length period just before (`step = -1`) or after (`step = 1`) a range:
 * a month steps a month, a quarter a quarter, and an odd range of days by its own length.
 */
export const shiftRange = (from, to, step) => {
  const f = parseYmd(from);
  const months = wholeMonths(from, to);
  if (months) {
    const s = new Date(f.getFullYear(), f.getMonth() + step * months, 1);
    const e = new Date(s.getFullYear(), s.getMonth() + months, 0);
    return { from: ymd(s), to: ymd(e) };
  }
  const t = parseYmd(to);
  const days = Math.round((t - f) / 864e5) + 1;
  return {
    from: ymd(new Date(f.getFullYear(), f.getMonth(), f.getDate() + step * days)),
    to: ymd(new Date(t.getFullYear(), t.getMonth(), t.getDate() + step * days)),
  };
};

/** This calendar month, start to end: what every report opens on. */
export const thisMonth = () => ({ from: monthStart(), to: monthEnd() });
